import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  MessageOutlined,
  WhatsAppOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';

import { AuthActions, UserActions } from '@portal/actions';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedTabs from '@portal/components/AdvancedTabs/AdvancedTabs';
import Loading from '@portal/components/Loading/Loading';
import SmsCode from '@portal/components/SmsCode/SmsCode';

import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';
import {
  unmaskField,
  isCpf,
  isCNPJ,
  maskCpfOrCnpj,
} from '@portal/services/masks';
import * as MessageService from '@portal/services/message';

import { passwordRules } from '@portal/config/app.json';
import {
  checkPasswordValidation,
  validatePassword,
} from '@portal/utils/validate';
import PasswordCheck from '@portal/components/PasswordCheck/PasswordCheck';
import { useNavigation } from '@portal/hooks/useNavigation';
import { TokenMethod } from '@portal/enum/device';

interface PasswordValidation {
  min: boolean;
  max: boolean;
  number?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  special?: boolean;
}

const defaultPasswordValidation: PasswordValidation = {
  min: false,
  max: false,
};

if (passwordRules.atLeastOneNumber) defaultPasswordValidation.number = false;
if (passwordRules.upperCase) defaultPasswordValidation.uppercase = false;
if (passwordRules.lowerCase) defaultPasswordValidation.lowercase = false;
if (passwordRules.specialCharacter) defaultPasswordValidation.special = false;

export enum TabKeys {
  ACCOUNT = 'account',
  METHOD = 'method',
  SMS = 'sms',
  CREDENTIALS = 'credentials',
}

const PasswordRecovery = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation<{ login?: boolean; userLogin: string }>();

  const {
    loading: { amount },
    theme: { currentTheme },
  } = useReduxState();

  const [currentTab, setCurrentTab] = useState<TabKeys>(TabKeys.ACCOUNT);
  const [taxIdentifier, setTaxIdentifier] = useState('');
  const [userLogin, setUserLogin] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<TokenMethod>(
    TokenMethod.SMS
  );

  const isLogin = !!navigate?.location?.state?.login;

  const handleTabChange = (key: string) => {
    const targetTab = key as TabKeys;
    const currentIndex = Object.values(TabKeys).indexOf(currentTab);
    const targetIndex = Object.values(TabKeys).indexOf(targetTab);

    if (targetIndex <= currentIndex) {
      setCurrentTab(targetTab);
    } else {
      return;
    }
  };

  const getDisabled = (tab: TabKeys) => {
    switch (tab) {
      case TabKeys.ACCOUNT: {
        const isValidTaxIdentifier =
          isCpf(unmaskField(taxIdentifier)) ||
          isCNPJ(unmaskField(taxIdentifier));
        const isCpfDocument = isCpf(unmaskField(taxIdentifier));
        return (
          !taxIdentifier ||
          (!isCpfDocument && !userLogin) ||
          !isValidTaxIdentifier
        );
      }
      case TabKeys.METHOD:
        return false;
      case TabKeys.SMS:
        return token.length !== 6;

      case TabKeys.CREDENTIALS:
        return !checkPasswordValidation(validatePassword(password));
      default:
        return false;
    }
  };

  const sendSmsCode = async () => {
    await dispatch(
      UserActions.checkToken(
        {
          taxIdentifier: unmaskField(taxIdentifier),
          login: userLogin,
          token,
        },
        (tab: string) => setCurrentTab(tab as TabKeys)
      )
    );
  };

  const submitAccountTab = async () => {
    const cleanTaxId = unmaskField(taxIdentifier);
    const isCpfDocument = isCpf(cleanTaxId);

    if (isCpfDocument && taxIdentifier) {
      setUserLogin(cleanTaxId);
      setCurrentTab(TabKeys.METHOD);
    } else if (taxIdentifier && userLogin) {
      setCurrentTab(TabKeys.METHOD);
    } else {
      MessageService.error('PAGES.AUTH.RECOVER.ERROR.MISSING_FIELDS', {
        domain: 'validation',
      });
    }
  };

  const submitMethodTab = async (method?: TokenMethod) => {
    if (taxIdentifier && userLogin) {
      const methodToUse = method ?? selectedMethod;
      await dispatch(
        AuthActions.recoveryPassword(
          {
            taxIdentifier: unmaskField(taxIdentifier),
            userLogin,
            method: methodToUse,
          },
          (tab: string) => setCurrentTab(tab as TabKeys)
        )
      );
    }
  };

  const handleSubmit = async () => {
    switch (currentTab) {
      case TabKeys.ACCOUNT:
        submitAccountTab();
        break;
      case TabKeys.METHOD:
        submitMethodTab();
        break;
      case TabKeys.SMS:
        sendSmsCode();

        break;
      case TabKeys.CREDENTIALS:
        dispatch(
          AuthActions.changePassword({
            password,
            taxIdentifier: unmaskField(taxIdentifier),
            token,
            login: userLogin,
          })
        );

        break;
      default:
        break;
    }
  };

  const getTitle = (tab: TabKeys) => {
    switch (tab) {
      case TabKeys.ACCOUNT:
        return translate('PAGES.AUTH.RECOVER.TABS.ACCOUNT.TITLE');
      case TabKeys.METHOD:
        return translate('PAGES.AUTH.RECOVER.TABS.METHOD.TITLE');
      case TabKeys.SMS:
        return selectedMethod === TokenMethod.WHATSAPP
          ? translate('PAGES.AUTH.RECOVER.TABS.SMS.TITLE_WHATSAPP')
          : translate('PAGES.AUTH.RECOVER.TABS.SMS.TITLE_SMS');
      case TabKeys.CREDENTIALS:
        return translate('PAGES.AUTH.RECOVER.TABS.CREDENTIALS.TITLE');
      default:
        return '';
    }
  };

  const getTabDisabled = (tab: TabKeys) => {
    const currentIndex = Object.values(TabKeys).indexOf(currentTab);
    const tabIndex = Object.values(TabKeys).indexOf(tab);
    return tabIndex > currentIndex;
  };

  const tabOptions = [
    {
      key: TabKeys.ACCOUNT,
      label: translate('PAGES.AUTH.RECOVER.TABS.ACCOUNT.LABEL'),
      disabled: getTabDisabled(TabKeys.ACCOUNT),
      children: (
        <div className="password-recovery__form__input">
          <AdvancedInput
            required
            label={translate(
              'PAGES.AUTH.RECOVER.TABS.ACCOUNT.FORM.TAX_IDENTIFIER.LABEL'
            )}
            placeholder={translate(
              'PAGES.AUTH.RECOVER.TABS.ACCOUNT.FORM.TAX_IDENTIFIER.PLACEHOLDER'
            )}
            value={maskCpfOrCnpj(taxIdentifier)}
            onChange={setTaxIdentifier}
          />
          {isCNPJ(unmaskField(taxIdentifier)) && (
            <AdvancedInput
              required
              label={translate(
                'PAGES.AUTH.RECOVER.TABS.ACCOUNT.FORM.LOGIN.LABEL'
              )}
              placeholder={translate(
                'PAGES.AUTH.RECOVER.TABS.ACCOUNT.FORM.LOGIN.PLACEHOLDER'
              )}
              value={userLogin}
              onChange={setUserLogin}
            />
          )}
        </div>
      ),
    },
    {
      key: TabKeys.METHOD,
      label: translate('PAGES.AUTH.RECOVER.TABS.METHOD.LABEL'),
      disabled: getTabDisabled(TabKeys.METHOD),
      children: (
        <div className="password-recovery__form__method">
          <div className="password-recovery__form__method__options">
            <div
              className={`password-recovery__form__method__option-card ${
                selectedMethod === TokenMethod.SMS
                  ? 'password-recovery__form__method__option-card--selected'
                  : ''
              }`}
              onClick={() => {
                setSelectedMethod(TokenMethod.SMS);
                submitMethodTab(TokenMethod.SMS);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedMethod(TokenMethod.SMS);
                  submitMethodTab(TokenMethod.SMS);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="password-recovery__form__method__option-card__icon password-recovery__form__method__option-card__icon--sms">
                <MessageOutlined />
              </div>
              <div className="password-recovery__form__method__option-card__content">
                <div className="password-recovery__form__method__option-card__title">
                  {translate('PAGES.AUTH.RECOVER.TABS.METHOD.SMS')}
                </div>
                <div className="password-recovery__form__method__option-card__description">
                  {translate('PAGES.AUTH.RECOVER.TABS.METHOD.SMS_DESCRIPTION')}
                </div>
              </div>
            </div>
            <div
              className={`password-recovery__form__method__option-card ${
                selectedMethod === TokenMethod.WHATSAPP
                  ? 'password-recovery__form__method__option-card--selected'
                  : ''
              }`}
              onClick={() => {
                setSelectedMethod(TokenMethod.WHATSAPP);
                submitMethodTab(TokenMethod.WHATSAPP);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedMethod(TokenMethod.WHATSAPP);
                  submitMethodTab(TokenMethod.WHATSAPP);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="password-recovery__form__method__option-card__icon password-recovery__form__method__option-card__icon--whatsapp">
                <WhatsAppOutlined />
              </div>
              <div className="password-recovery__form__method__option-card__content">
                <div className="password-recovery__form__method__option-card__title">
                  {translate('PAGES.AUTH.RECOVER.TABS.METHOD.WHATSAPP')}
                </div>
                <div className="password-recovery__form__method__option-card__description">
                  {translate(
                    'PAGES.AUTH.RECOVER.TABS.METHOD.WHATSAPP_DESCRIPTION'
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: TabKeys.SMS,
      label: translate('PAGES.AUTH.RECOVER.TABS.SMS.LABEL'),
      disabled: getTabDisabled(TabKeys.SMS),
      children: (
        <div className="password-recovery__form__sms">
          <SmsCode
            code={token}
            setCode={setToken}
            method={selectedMethod}
            onClickResend={(method) => {
              if (method) {
                setSelectedMethod(method);
                submitMethodTab(method);
              } else {
                submitMethodTab();
              }
            }}
          />
        </div>
      ),
    },
    {
      key: TabKeys.CREDENTIALS,
      label: translate('PAGES.AUTH.RECOVER.TABS.CREDENTIALS.LABEL'),
      disabled: getTabDisabled(TabKeys.CREDENTIALS),
      children: (
        <>
          <AdvancedInput
            label={translate(
              'PAGES.AUTH.RECOVER.TABS.CREDENTIALS.FORM.PASSWORD.LABEL'
            )}
            placeholder={translate(
              'PAGES.AUTH.RECOVER.TABS.CREDENTIALS.FORM.PASSWORD.PLACEHOLDER'
            )}
            value={password}
            onChange={setPassword}
            style={{
              marginBottom: '10px',
            }}
            securedTextField={!showPassword}
            endIcon={
              showPassword ? (
                <EyeInvisibleOutlined onClick={() => setShowPassword(false)} />
              ) : (
                <EyeOutlined onClick={() => setShowPassword(true)} />
              )
            }
            maxLength={passwordRules.maxLength}
          />
          <PasswordCheck
            password={password}
            passwordValidation={validatePassword(password)}
          />
        </>
      ),
    },
  ];

  return (
    <>
      {amount > 0 && <Loading />}
      <div
        className="password-recovery"
        style={{ background: currentTheme.background }}
      >
        <div className="password-recovery__form">
          <img src="assets/svg/lock.svg" alt="lock" />
          <h1>
            {isLogin
              ? translate('PAGES.AUTH.RECOVER.TITLE.LOGIN')
              : translate('PAGES.AUTH.RECOVER.TITLE.PASSWORD')}
          </h1>
          <p>{getTitle(currentTab)}</p>
          <AdvancedTabs
            items={tabOptions}
            activeKey={currentTab}
            onChange={handleTabChange}
          />
          {currentTab !== TabKeys.METHOD && (
            <div className="password-recovery__form__button">
              <AdvancedButton
                fullWidth
                text={translate('PAGES.AUTH.RECOVER.CONTINUE')}
                onClick={handleSubmit}
                disabled={getDisabled(currentTab)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PasswordRecovery;
